/**
 * @file ShippingView.tsx
 *
 * Shipping tab screen (overview). On mount it loads the zones (with their methods
 * and locations and the region tree) and the pickup settings, then renders the
 * two sections — custom shipping and local pickup. It owns the screen-level
 * loading and error states; each section renders its own card, table, and
 * reorder/edit/delete actions. Extra table columns collapse on mobile inside the
 * shared {@link DraggableDataTable}.
 */

import { useEffect } from 'react';
import { Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { useShippingStore } from '../stores/shipping.store';
import { EmptyState } from '../components/EmptyState';
import { CustomShippingSection } from './CustomShippingSection';
import { LocalPickupSection } from './LocalPickupSection';
import shared from '../shared.module.css';
import styles from './ShippingView.module.css';

/**
 * Renders the Shipping tab.
 *
 * @returns The shipping overview screen.
 */
export function ShippingView() {
  const intl = useIntl();

  const zones = useShippingStore((s) => s.zones);
  const pickups = useShippingStore((s) => s.pickupLocations);
  const loading = useShippingStore((s) => s.loading);
  const error = useShippingStore((s) => s.error);
  const pickupLoading = useShippingStore((s) => s.pickupLoading);
  const pickupError = useShippingStore((s) => s.pickupError);
  const fetchShipping = useShippingStore((s) => s.fetchShipping);
  const fetchPickupLocations = useShippingStore((s) => s.fetchPickupLocations);

  useEffect(() => {
    fetchShipping();
    fetchPickupLocations();
  }, [fetchShipping, fetchPickupLocations]);

  const hasData = zones.length > 0 || pickups.length > 0;

  if ((loading || pickupLoading) && !hasData) {
    return (
      <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
        <Loader>
          <FormattedMessage {...adminMessages.shippingLoading} />
        </Loader>
      </div>
    );
  }

  if ((error || pickupError) && !hasData) {
    return (
      <EmptyState
        testId="shipping-error"
        title={intl.formatMessage(adminMessages.shippingErrorTitle)}
        description={intl.formatMessage(adminMessages.shippingErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.shippingRetry),
          onClick: () => {
            void fetchShipping();
            void fetchPickupLocations();
          },
        }}
      />
    );
  }

  return (
    <div className={`${styles.container} ${shared.outerContainer}`}>
      <div className="gv-flex gv-flex-col gv-gap-sm">
        <h2 className={`gv-heading-lg ${styles.title}`}>
          <FormattedMessage {...adminMessages.shippingTitle} />
        </h2>
        <div className="gv-text-sm">
          <FormattedMessage {...adminMessages.shippingDescription} />
        </div>
      </div>
      <div className={styles.sections}>
        <CustomShippingSection />
        <LocalPickupSection />
      </div>
    </div>
  );
}
